import React, { useCallback, useEffect, useRef, useState } from 'react';

const MAX_BATCH = 250;
const requestId = () => window.crypto?.randomUUID?.() || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  const r = Math.random() * 16 | 0;
  return (c === 'x' ? r : (r & 3) | 8).toString(16);
});

const Boss = ({ clanId, userId }) => {
  const [bossHP, setBossHP] = useState(0);
  const [ready, setReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const storageKey = `boss-hits:${userId}:${clanId}`;
  const persist = useCallback(() => { try { localStorage.setItem(storageKey, JSON.stringify({ pending: pendingRef.current, batch: retryRef.current })); } catch {} }, [storageKey]);
  const [maxHP, setMaxHP] = useState(100000);
  const [pendingDamage, setPendingDamage] = useState(0);
  const [syncState, setSyncState] = useState('ready');
  const [rewards, setRewards] = useState([]);
  const [bossDefeated, setBossDefeated] = useState(false);
  const [loading, setLoading] = useState(true);
  const pendingRef = useRef(0);
  const inFlightRef = useRef(false);
  const retryRef = useRef(null);

  const fetchRewards = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/boss/${clanId}/rewards/${userId}`);
      if (response.ok) setRewards((await response.json()).rewards || []);
    } catch (error) { console.error('Ошибка загрузки наград:', error); }
    finally { setLoading(false); }
  }, [clanId, userId]);

  const flushDamage = useCallback(async () => {
    if (!clanId || inFlightRef.current || (!retryRef.current && pendingRef.current <= 0)) return;
    const batch = retryRef.current || { requestId: requestId(), damage: Math.min(pendingRef.current, MAX_BATCH) };
    retryRef.current = batch;
    persist();
    inFlightRef.current = true;
    setSyncState('syncing');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/boss/${clanId}/damage_boss`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(batch), keepalive: true, signal: AbortSignal.timeout(12000),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Ошибка ${response.status}`);
      pendingRef.current = Math.max(0, pendingRef.current - batch.damage);
      setPendingDamage(pendingRef.current);
      setBossHP(Math.max(0, Number(data.boss_health) - pendingRef.current));
      retryRef.current = null;
      persist();
      setErrorMessage('');
      setSyncState('ready');
      if (Number(data.boss_health) === 0) { setBossDefeated(true); fetchRewards(); }
    } catch (error) {
      retryRef.current = batch;
      setSyncState('retry');
      setErrorMessage(error.message);
      persist();
      console.error('Ошибка отправки урона:', error);
    } finally { inFlightRef.current = false; }
  }, [clanId, fetchRewards, persist]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved && Number.isInteger(saved.pending) && saved.pending >= 0) { pendingRef.current = saved.pending; retryRef.current = saved.batch; setPendingDamage(saved.pending); }
    } catch {}
  }, [storageKey]);

  useEffect(() => {
    if (!clanId) return undefined;
    const fetchHP = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/boss/${clanId}/boss_hp`);
        if (!response.ok) throw new Error('Не удалось загрузить босса');
        const { boss_health: hp, boss_max_health: maximum } = await response.json();
        setReady(true);
        if (inFlightRef.current) return;
        if (Number(maximum) > 0) setMaxHP(Number(maximum));
        if (hp === null || Number(hp) === 0) {
          setBossDefeated(true);
          if (hp !== null && Number(hp) === 0) fetchRewards();
          else setLoading(false);
        } else setBossHP(Math.max(0, Number(hp) - pendingRef.current));
      } catch (error) { setErrorMessage(error.message); }
    };
    fetchHP();
    const timer = setInterval(fetchHP, 3000);
    return () => clearInterval(timer);
  }, [clanId, fetchRewards]);

  useEffect(() => {
    const timer = setInterval(flushDamage, 1500);
    const onHide = () => { if (document.visibilityState === 'hidden') flushDamage(); };
    document.addEventListener('visibilitychange', onHide);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onHide); flushDamage(); };
  }, [flushDamage]);

  const attack = () => {
    if (!ready || bossHP <= 0 || bossDefeated) return;
    pendingRef.current += 1;
    persist();
    setPendingDamage(pendingRef.current);
    setBossHP((hp) => Math.max(0, hp - 1));
  };

  const hpPercent = Math.max(0, Math.min(100, bossHP / maxHP * 100));
  const syncLabel = syncState === 'syncing' ? 'Сохраняем удары…' : syncState === 'retry'
    ? 'Повторяем сохранение…' : pendingDamage ? 'Удары в очереди' : 'Удары сохранены';

  return <main className="boss-arena">
    <header className="boss-arena__header"><span>Еженедельный рейд</span><h1>Хранитель клана</h1><p>Сражайтесь вместе. Каждый удар приближает к общей награде.</p></header>
    {!ready ? <section className="boss-card"><p>{errorMessage || 'Загружаем рейд…'}</p></section> : bossDefeated ? <section className="boss-victory"><b className="boss-victory__seal">✦</b><h2>Босс побеждён!</h2>
      {loading ? <p>Подготавливаем награды…</p> : rewards.length ? <div className="boss-rewards">{rewards.map((reward, i) =>
        <article className="boss-reward" key={`${reward.item.id}-${i}`}><img src={reward.item.image_url} alt=""/><div><strong>{reward.item.name}</strong><span>+{reward.item.click_power} к силе клика</span></div></article>)}</div>
        : <p>Награда появится в инвентаре, если вы участвовали в битве.</p>}</section>
      : <section className="boss-card">
        <div className="boss-card__status"><div><span>Здоровье босса</span><strong>{bossHP.toLocaleString('ru-RU')} / {maxHP.toLocaleString('ru-RU')}</strong></div><span className={`boss-sync boss-sync--${syncState}`}>{syncLabel}</span></div>
        <div className="boss-hp"><div style={{ width: `${hpPercent}%` }}/></div>
        <button type="button" className="boss-target" onClick={attack}><span/><img src="/assets/imgur/eL7tDMO.jpg" alt="Хранитель клана"/><em>Нажимайте, чтобы атаковать</em></button>
        <div className="boss-actions"><button type="button" onClick={attack}>Нанести удар</button><div><span>Серия</span><strong>{pendingDamage}</strong></div></div>
        {errorMessage && <p className="raid-error" role="status">{errorMessage}. Удары остаются в очереди.</p>}
      </section>}
  </main>;
};

export default Boss;
